"use client";

import { type FormEvent, useEffect, useState } from "react";
import { Clock3, Mail, MapPin, MessageCircle, Phone, UserRound } from "lucide-react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";
import { useFormValidation } from "@/app/hooks/useFormValidation";
import {
  checked,
  email as emailValidator,
  minLength,
  personName,
  phone as phoneValidator,
  required,
} from "@/app/utils/validation";
import { FORM_FIELDS } from "@/app/utils/formFields";
import { geoService, type Prediction } from "../services/geoService";
import { useGeo } from "../context/GeoContext";
import CallPopup from "./callBack";
import { Checkbox } from "../design-system/atoms/Checkbox";
import { FieldError } from "../design-system/atoms/FieldError";
import { cn } from "../design-system/lib/utils";
import { useAgency } from "../context/AgencyContext";

type ContactData = {
  id?: number;
  documentId?: string;
  titre?: string | null;
};

type ContactezNousBlock = StrapiBlock & {
  __component: "blocks.contactez-nous";
  titre?: string | null;
  adresse?: string | null;
  telephone?: string | null;
  horaire?: string | null;
  contact?: ContactData | null;
};

type FormValues = {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  adresse: string;
  message: string;
  consentement: boolean;
  consentEmail: boolean;
};

const INITIAL_VALUES: FormValues = {
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  adresse: "",
  message: "",
  consentement: false,
  consentEmail: false,
};

// Extrait rue / code postal / ville depuis une adresse complète
// (regex sur le code postal, fallback sur l'API api-adresse.data.gouv.fr)
const parseAddress = async (addressText: string) => {
  const trimmedAddress = addressText.trim();

  const addressPatternWithPostcode = /^(.+?)\s*,?\s*(\d{5})\s+(.+)$/;
  const match = trimmedAddress.match(addressPatternWithPostcode);

  if (match) {
    return {
      street: match[1].trim().replace(/,\s*$/, ""),
      postcode: match[2],
      city: match[3].split(",")[0].trim(),
    };
  }

  try {
    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(trimmedAddress)}&limit=1`
    );
    const geocodingData = await response.json();
    if (geocodingData.features && geocodingData.features.length > 0) {
      const feature = geocodingData.features[0].properties;
      return {
        street: feature.name || "",
        postcode: feature.postcode || "",
        city: feature.city || "",
      };
    }
  } catch (error) {
    console.warn("Erreur geocoding:", error);
  }

  return { street: trimmedAddress, postcode: "", city: "" };
};

export default function ContactezNous({ data }: BlockProps<ContactezNousBlock>) {
  const phoneHref = data.telephone?.replace(/\s+/g, "") || "";

  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Autocomplétion adresse
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [addressDetails, setAddressDetails] = useState({
    street: "",
    postcode: "",
    city: "",
    idSalesforce: "",
  });

  // Encart "appel immédiat" affiché quand l'agence de l'adresse a showIdelioButton
  const [showIdelioBox, setShowIdelioBox] = useState(false);
  const [callbackChecked, setCallbackChecked] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);

  const { setAgency, setHasSelected } = useGeo();

  const { errors, validateField, validateAll, clearErrors } = useFormValidation({
    nom: [
      required("Veuillez saisir votre nom."),
      personName("Veuillez saisir un nom valide (au moins 2 lettres)."),
    ],
    prenom: [
      required("Veuillez saisir votre prénom."),
      personName("Veuillez saisir un prénom valide (au moins 2 lettres)."),
    ],
    email: [
      required("Veuillez saisir votre adresse e-mail."),
      emailValidator(),
    ],
    telephone: [
      required("Veuillez saisir votre téléphone."),
      phoneValidator("Veuillez saisir un numéro de téléphone français valide."),
    ],
    adresse: [required("Veuillez saisir votre adresse de livraison.")],
    message: [
      required("Veuillez saisir votre message."),
      minLength(10, "Votre message doit contenir au moins 10 caractères."),
    ],
    consentement: [
      checked("Vous devez accepter l'utilisation de vos données."),
    ],
    consentEmail: [
      checked("Vous devez valider votre autorisation de contact par e-mail."),
    ],
  });

  // Suggestions d'adresses (debounce 500ms) — mêmes filtres que Contact
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (values.adresse.length >= 3) {
        try {
          const result = await geoService.getPredictions(values.adresse);
          const addresses = result.filter((d: Prediction) =>
            d.types.some((t) =>
              ["subpremise", "street_address", "geocode"].includes(t)
            )
          );
          setPredictions(addresses);
          setShowPredictions(true);
        } catch (err) {
          console.error("Erreur API predictions:", err);
        }
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [values.adresse]);

  const getAgencyDetails = async (placeId: string) => {
    try {
      const agency = await geoService.getMap(placeId);

      if (agency && agency.id) {
        return agency;
      }

      return await geoService.getNearestAgency(placeId);
    } catch (error) {
      console.error("Erreur getMap/getNearestAgency:", error);
      return null;
    }
  };

  const updateValue = (name: keyof FormValues, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleSelectPrediction = async (prediction: Prediction) => {
    const placeId = prediction.placeId;
    setShowPredictions(false);
    updateValue("adresse", prediction.description);

    try {
      const parsed = await parseAddress(prediction.description);
      const agencyDetails = await getAgencyDetails(placeId);

      // L'agence propose l'appel immédiat : on la persiste
      // pour la popup CallBack et on affiche l'encart vert
      const idelioAllowed =
        agencyDetails?.showIdelioButton === true && !!agencyDetails?.id;

      setShowIdelioBox(idelioAllowed);
      setCallbackChecked(false);
      if (idelioAllowed && agencyDetails) {
        setAgency({
          ...agencyDetails,
          phoneIdelio: agencyDetails.phoneIdelio || "",
        });
        setHasSelected(true);
      }

      setAddressDetails({
        street: parsed.street,
        postcode: parsed.postcode,
        city: parsed.city,
        idSalesforce: agencyDetails?.idSalesforce || "",
      });
    } catch (err) {
      console.error("Erreur sélection adresse:", err);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateAll({ ...values })) {
      setSubmitStatus("idle");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      // Paramètres UTM et gclid de l'URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get("utm_source");
      const utmCampaign = urlParams.get("utm_campaign");
      const utmContent = urlParams.get("utm_content");
      const utmTerm = urlParams.get("utm_term");
      const utmMedium = urlParams.get("utm_medium");
      const gclid = urlParams.get("gclid");

      // Fallback : parser l'adresse saisie si pas de sélection de suggestion
      let { street, postcode, city } = addressDetails;
      if (!street || !city || !postcode) {
        const parsed = await parseAddress(values.adresse);
        street = parsed.street;
        postcode = parsed.postcode;
        city = parsed.city;
      }
      const idSalesforce = addressDetails.idSalesforce;

      const subject = `Formulaire contact agence - ${values.prenom} ${values.nom}`;

      // Même structure de payload que le formulaire Contact
      const submissionData = {
        data: {
          firstname: values.prenom,
          lastname: values.nom,
          phone: values.telephone,
          email: values.email,
          subject,
          message: values.message,
          utm_source: utmSource || "",
          utm_campaign: utmCampaign || "",
          utm_content: utmContent || "",
          utm_term: utmTerm || "",
          utm_medium: utmMedium || "",
          gclid: gclid || "",
          type: "Agence",
          priority: "Mineur",
          encoding: "UTF-8",

          // Consentements
          consent_gdpr: values.consentement,
          consent_email: values.consentEmail,

          // Données d'adresse détaillées
          street,
          postcode,
          city,
          address: values.adresse, // Adresse brute saisie (utilisée pour l'email de notification)
          AccountID: idSalesforce,

          // Champs Salesforce (noms de champs spécifiques à Salesforce)
          ...(FORM_FIELDS.LASTNAME && { [FORM_FIELDS.LASTNAME]: values.nom }),
          ...(FORM_FIELDS.FIRSTNAME && { [FORM_FIELDS.FIRSTNAME]: values.prenom }),
          ...(FORM_FIELDS.STREET && { [FORM_FIELDS.STREET]: street }),
          ...(FORM_FIELDS.CITY && { [FORM_FIELDS.CITY]: city }),
          ...(FORM_FIELDS.POSTCODE && { [FORM_FIELDS.POSTCODE]: postcode }),
          ...(FORM_FIELDS.ACCOUNT_ID && { [FORM_FIELDS.ACCOUNT_ID]: idSalesforce }),
          ...(FORM_FIELDS.ACQUISITION_CHANNEL && { [FORM_FIELDS.ACQUISITION_CHANNEL]: "Formulaire" }),
          ...(FORM_FIELDS.PHONE_INDICATOR && { [FORM_FIELDS.PHONE_INDICATOR]: "FR" }),
          acquisition_channel: "Formulaire",
          phone_indicator: "FR",
        },
      };

      // Enregistrement dans Strapi "Contacts Agences" (+ envoi Salesforce côté back)
      const endpoint = process.env.NEXT_PUBLIC_CONTACT_AGENCE_ENDPOINT;
      const response = await fetch(new URL(endpoint).href, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error?.message || "Erreur lors de l'envoi du formulaire"
        );
      }

      // L'email de notification est envoyé côté back (sendMailWp) par le endpoint /contact-agences
      setSubmitStatus("success");
      setValues(INITIAL_VALUES);
      clearErrors();
      setAddressDetails({ street: "", postcode: "", city: "", idSalesforce: "" });
      setPredictions([]);
      setShowPredictions(false);
      setShowIdelioBox(false);
      setCallbackChecked(false);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { apiBackend } = useAgency();

  const adresse = apiBackend?.Adresse;

  const telephone = apiBackend?.Telephone;

  const email = apiBackend?.Email;

  const horaire = apiBackend?.Schedule;

  return (
    <section className="bg-white">
      <div className="lms-container">
        <div className="grid min-h-[650px] lg:grid-cols-[40%_60%]">

          {/* INFORMATIONS AGENCE */}
          <div className="flex items-center py-14 pr-6 lg:py-20 lg:pr-14">
            <div className="w-full max-w-[430px]">
              {data.titre && (
                <h2 className="font-playfair text-3xl font-bold leading-tight text-primaryGreen lg:text-[44px]">
                  {data.titre}
                </h2>
              )}

              <div className="mt-10 space-y-5">
                {adresse && (
                  <div className="flex items-center gap-4">
                    <MapPin size={34} strokeWidth={1.7} className="shrink-0 text-primaryGreen" />

                    <div className="h-8 w-[2px] bg-primaryGreen" />

                    <p className="text-base font-bold text-textColor lg:text-lg">
                      {adresse}
                    </p>
                  </div>
                )}

                {telephone && (
                  <div className="flex items-center gap-4">
                    <Phone size={34} strokeWidth={1.7} className="shrink-0 text-primaryGreen" />

                    <div className="h-8 w-[2px] bg-primaryGreen" />

                    <a
                      href={`tel:${phoneHref}`}
                      className="text-base font-bold text-textColor transition-colors hover:text-primaryGreen lg:text-lg"
                    >
                      {telephone}
                    </a>
                  </div>
                )}

                {horaire && (
                  <div className="flex items-center gap-4">
                    <Clock3 size={34} strokeWidth={1.7} className="shrink-0 text-primaryGreen" />

                    <div className="h-8 w-[2px] bg-primaryGreen" />

                    <p className="text-base font-bold text-textColor lg:text-lg">
                      {horaire}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* FORMULAIRE */}
          <div className="bg-[#F6F6F6] px-6 py-14 lg:px-16 lg:py-16">
            {data.contact?.titre && (
              <h2 className="mb-9 text-center font-playfair text-4xl font-bold text-textColor lg:text-[44px]">
                {data.contact.titre}
              </h2>
            )}

            <form className="mx-auto max-w-[680px]" onSubmit={handleSubmit} noValidate>
              {/* NOM + PRENOM */}
              <div className="grid gap-5 sm:grid-cols-2">
                <InputField
                  icon={<UserRound size={17} />}
                  name="nom"
                  placeholder="Nom *"
                  value={values.nom}
                  error={errors.nom}
                  onChange={(e) => updateValue("nom", e.target.value)}
                  onBlur={(e) => validateField("nom", e.target.value)}
                />

                <InputField
                  icon={<UserRound size={17} />}
                  name="prenom"
                  placeholder="Prénom *"
                  value={values.prenom}
                  error={errors.prenom}
                  onChange={(e) => updateValue("prenom", e.target.value)}
                  onBlur={(e) => validateField("prenom", e.target.value)}
                />
              </div>

              {/* EMAIL + TELEPHONE */}
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <InputField
                  icon={<Mail size={17} />}
                  name="email"
                  type="email"
                  placeholder="Email *"
                  value={values.email}
                  error={errors.email}
                  onChange={(e) => updateValue("email", e.target.value)}
                  onBlur={(e) => validateField("email", e.target.value)}
                />

                <InputField
                  icon={<Phone size={17} />}
                  name="telephone"
                  type="tel"
                  placeholder="Téléphone *"
                  value={values.telephone}
                  error={errors.telephone}
                  onChange={(e) => updateValue("telephone", e.target.value)}
                  onBlur={(e) => validateField("telephone", e.target.value)}
                />
              </div>

              {/* ADRESSE avec autocomplétion */}
              <div className="mt-5 relative">
                <InputField
                  icon={<MapPin size={17} />}
                  name="adresse"
                  placeholder="Adresse de livraison *"
                  value={values.adresse}
                  error={errors.adresse}
                  autoComplete="off"
                  onChange={(e) => updateValue("adresse", e.target.value)}
                  onFocus={() =>
                    predictions.length > 0 && setShowPredictions(true)
                  }
                  onBlur={(e) => {
                    validateField("adresse", e.target.value);
                    setTimeout(() => setShowPredictions(false), 300);
                  }}
                />

                {showPredictions && predictions.length > 0 && (
                  <ul
                    className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg"
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {predictions.map((prediction) => (
                      <li
                        key={prediction.placeId}
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                        onMouseDown={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          await handleSelectPrediction(prediction);
                        }}
                      >
                        {prediction.description}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Encart vert : l'agence de l'adresse propose l'appel immédiat */}
                {showIdelioBox && (
                  <div className="mt-3 flex items-center gap-5 rounded-xl border border-primaryGreen/40 bg-secondaryGreen px-6 py-5">
                    {/* Icône téléphone dans un cercle vert */}
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent">
                      <Phone size={26} className="text-white" />
                    </span>

                    {/* Texte */}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <p className="text-lg font-bold text-dark">
                          Oui, rappelez-moi tout de suite !
                        </p>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase text-white">
                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                          En ligne
                        </span>
                      </div>
                      <p className="mt-1.5 text-base text-textColor">
                        Cochez cette case et envoyez le formulaire : un conseiller
                        vous rappelle en quelques secondes au numéro indiqué —
                        gratuit, sans engagement.
                      </p>
                    </div>

                    {/* Checkbox custom (bord verte, fond blanc) */}
                    <span className="relative inline-flex shrink-0">
                      <input
                        type="checkbox"
                        id="idelio-callback-agence"
                        aria-label="Être rappelé immédiatement par un conseiller"
                        checked={callbackChecked}
                        onChange={(e) => {
                          setCallbackChecked(e.target.checked);
                          if (e.target.checked) setShowCallPopup(true);
                        }}
                        className="peer h-7 w-7 cursor-pointer appearance-none rounded-md border-2 border-accent bg-white transition-colors checked:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                      />
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                      >
                        <path
                          d="M4 10.5l4 4 8-9"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                )}
              </div>

              {/* MESSAGE */}
              <div className="mt-7">
                <div className="relative">
                  <MessageCircle
                    size={17}
                    className="pointer-events-none absolute left-4 top-4 text-textColor"
                  />

                  <textarea
                    name="message"
                    placeholder="Message *"
                    rows={4}
                    value={values.message}
                    onChange={(e) => updateValue("message", e.target.value)}
                    onBlur={(e) => validateField("message", e.target.value)}
                    aria-invalid={errors.message ? true : undefined}
                    className={cn(
                      "w-full resize-none rounded-md border border-[#C9C9C9] bg-white py-4 pl-11 pr-4 text-sm text-textColor outline-none transition-colors placeholder:text-[#777] focus:border-primaryGreen",
                      errors.message && "border-red-500 focus:border-red-500"
                    )}
                  />
                </div>
                <FieldError message={errors.message} />
              </div>

              {/* CONSENTEMENT */}
              <div className="mt-5">
                <label className="flex cursor-pointer font-medium items-start gap-3 text-lg leading-relaxed text-textColor">
                  <input
                    type="checkbox"
                    name="consentement"
                    checked={values.consentement}
                    onChange={(e) => updateValue("consentement", e.target.checked)}
                    aria-invalid={errors.consentement ? true : undefined}
                    className={cn(
                      "mt-[2px] h-4 w-4 accent-primaryGreen",
                      errors.consentement && "accent-red-600"
                    )}
                  />

                  <span>
                    J&apos;accepte que mes données soient utilisées pour traiter ma demande conformément à la{" "}
                    <a href="/politique-de-confidentialite" className="underline">
                      politique de confidentialité
                    </a>.
                  </span>
                </label>
                <FieldError message={errors.consentement} />
              </div>

              {/* AUTORISATION DE CONTACT — identique au formulaire Contact */}
              <div className="mt-6">
                <Checkbox
                  id="consent_email"
                  name="consent_email"
                  label="Pour pouvoir vous contacter et répondre rapidement à votre demande, merci de valider votre autorisation de contact par e-mail."
                  required
                  checked={values.consentEmail}
                  onChange={(e) => updateValue("consentEmail", e.target.checked)}
                  error={errors.consentEmail}
                />
              </div>

              {/* MESSAGES DE STATUT */}
              {submitStatus === "success" && (
                <div className="mt-6 rounded-lg bg-green-100 p-4 text-sm text-green-700">
                  Votre message a été envoyé avec succès. Nous vous recontacterons rapidement.
                </div>
              )}

              {submitStatus === "error" && errorMessage && (
                <div className="mt-6 rounded-lg bg-red-100 p-4 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              {/* BOUTON */}
              <div className="mt-10 flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="min-w-[180px] rounded-md bg-[#ED174C] px-8 py-4 text-sm font-bold uppercase text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>

      {/* Popup webcallback (appel immédiat) */}
      {showCallPopup && (
        <CallPopup
          adresse={values.adresse}
          onClose={() => {
            setShowCallPopup(false);
            setCallbackChecked(false);
          }}
        />
      )}
    </section>
  );
}

type InputFieldProps = {
  name: string;
  placeholder: string;
  type?: string;
  icon: React.ReactNode;
  value: string;
  error?: string;
  autoComplete?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

function InputField({
  name,
  placeholder,
  type = "text",
  icon,
  value,
  error,
  autoComplete,
  onChange,
  onBlur,
  onFocus,
}: InputFieldProps) {
  return (
    <div>
      <div className="relative">
        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#777]">
          {icon}
        </div>

        <input
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          className={cn(
            "h-[52px] w-full rounded-md border border-[#C9C9C9] bg-white pl-11 pr-4 text-sm text-textColor outline-none transition-colors placeholder:text-[#777] focus:border-primaryGreen",
            error && "border-red-500 focus:border-red-500"
          )}
        />
      </div>
      <FieldError message={error} />
    </div>
  );
}
